import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { IconComponent } from '@app/components/ui-icon/icon.component';
import { IconName } from '@app/components/ui-icon/icon-registry';
import { SeoSegmentsToken } from '@app/core/env.tokens';
import { LocalizationService } from '@services/logic/localization.service';
import type { CountryCode } from '@handyhand/i18n';

interface ServiceOption {
  labelKey: string;
  path: Record<CountryCode, string>;
  icon: IconName;
}

const SERVICE_OPTIONS: ServiceOption[] = [
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.HANDYMAN',
    path: { dk: '/handyman', nl: '/klusjesman' },
    icon: 'wrench-01',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.GARDENING',
    path: { dk: '/have/havearbejde', nl: '/tuin/tuinwerk' },
    icon: 'plant-01',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.CARPENTER',
    path: { dk: '/toemrer', nl: '/timmerman' },
    icon: 'tools',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.PLUMBING',
    path: { dk: '/vvs-arbejde/vvs', nl: '/loodgieter-werk/dakenmaker' },
    icon: 'droplet',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.PAINTER',
    path: { dk: '/maler', nl: '/schilder' },
    icon: 'paint-brush-02',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.WASTE_COLLECTION',
    path: { dk: '/affald/affald-service', nl: '/afval/afvalverwijdering' },
    icon: 'garbage-truck',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.ELECTRICIAN',
    path: { dk: '/elektriker', nl: '/elektricien' },
    icon: 'zap',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.TECHNICIAN',
    path: { dk: '/it/it-support', nl: '/it/hulp-bij-mobiel-pc-tv' },
    icon: 'computer',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.MOVING_HELP',
    path: { dk: '/flytning/flyttemand', nl: '/verhuizen/verhuizer' },
    icon: 'delivery-truck-01',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.FURNITURE_ASSEMBLY',
    path: {
      dk: '/moebler/moebel-montoer',
      nl: '/meubels/meubelverzameling-en-montage',
    },
    icon: 'chair-01',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.CLEANING',
    path: { dk: '/rengoering', nl: '/schoonmaak' },
    icon: 'clean',
  },
  {
    labelKey: 'PAGES.LANDING.SERVICE_OPTIONS.WINDOW_CLEANING',
    path: { dk: '/rengoering/vinduespudser', nl: '/schoonmaak/raamreiniging' },
    icon: 'sparkles',
  },
];

@Component({
  imports: [TranslatePipe, IconComponent],
  selector: 'app-service-options',
  templateUrl: './serviceOptionsComponent.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./serviceOptionsComponent.scss'],
})
export class ServiceOptionsComponent {
  private readonly router = inject(Router);
  private readonly localizationService = inject(LocalizationService);
  private readonly seg = inject(SeoSegmentsToken);

  public readonly services = SERVICE_OPTIONS.map((service) => ({
    labelKey: service.labelKey,
    link: `${service.path[this.localizationService.routingCountryCode]}/${this.seg.suffix.serviceVertical}`,
    icon: service.icon,
  }));

  public navigateToCategories() {
    return this.router.navigateByUrl(
      this.localizationService.seoRoute('categories'),
    );
  }
}
